/**
 * Templates settings list state (issue #98).
 *
 * Only the list and the delete live here. Create and update are issued directly
 * by TemplateModal against budgetTemplatesApi, because a thunk collapses the
 * backend's field errors ("a template with this name already exists") into a
 * bare "Request failed with status code 400". The modal refetches through
 * fetchPaginatedTemplates on success.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import budgetTemplatesApi, { BudgetTemplateListItem } from '../../api/budgetTemplatesApi'
import { RootState } from '../rootState'
import { IPagination } from '../../types/pagination'

export interface TemplatesState {
  paginatedTemplates: IPagination<BudgetTemplateListItem[]>
  loading: boolean
  error: string | null
  currentPage: number
  searchQuery: string
  sortField: string
  sortDirection: 'asc' | 'desc'
}

const initialState: TemplatesState = {
  paginatedTemplates: {
    count: 0,
    links: {
      next: null,
      previous: null
    },
    page_size: 10,
    results: [],
    current_page: 1,
    total_pages: 1,
    last_page: 1,
    from: 0,
    to: 0
  },
  loading: false,
  error: null,
  currentPage: 1,
  searchQuery: '',
  sortField: 'name',
  sortDirection: 'asc'
}

export const fetchPaginatedTemplates = createAsyncThunk(
  'templates/fetchPaginatedTemplates',
  async (params: {
    page?: number
    page_size?: number
    search?: string
    order?: string
  }) => {
    return budgetTemplatesApi.getPaginatedTemplates(params)
  }
)

export const deleteTemplate = createAsyncThunk(
  'templates/deleteTemplate',
  async (id: number) => {
    await budgetTemplatesApi.deleteTemplate(id)
    return id
  }
)

const templatesSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload
      state.currentPage = 1
    },
    setSortField: (state, action) => {
      state.sortField = action.payload
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaginatedTemplates.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPaginatedTemplates.fulfilled, (state, action) => {
        state.loading = false
        state.paginatedTemplates = action.payload
      })
      .addCase(fetchPaginatedTemplates.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch templates'
      })
      .addCase(deleteTemplate.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteTemplate.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(deleteTemplate.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to delete template'
      })
  }
})

export const {
  clearError,
  setCurrentPage,
  setSearchQuery,
  setSortField,
  setSortDirection
} = templatesSlice.actions

// Selectors
export const selectPaginatedTemplates = (state: RootState) => state.templates.paginatedTemplates
export const selectTemplatesLoading = (state: RootState) => state.templates.loading
export const selectTemplatesError = (state: RootState) => state.templates.error
export const selectCurrentPage = (state: RootState) => state.templates.currentPage
export const selectSearchQuery = (state: RootState) => state.templates.searchQuery
export const selectSortField = (state: RootState) => state.templates.sortField
export const selectSortDirection = (state: RootState) => state.templates.sortDirection

export default templatesSlice.reducer
